const getUniqueID = () => (crypto.randomUUID && crypto.randomUUID()) ||
  `${Date.now()}_${Math.floor(Math.random() * 10000)}`

/**
 * Get bundle from line items
 * @param bundleConfig
 * @param items
 * @returns {null|{add: {attributes: [{key: string, value: string}], id: string, quantity: number}, updates: {quantity: *, id: *}[]}}
 */
export function getBundleFromLineItems(bundleConfig, items) {
  const bundle = [];
  const lineItems = items.filter(item => !item.has_components).map((item) => ({...item}));

  for (const category of bundleConfig.categories) {
    const { requiredQty } = category;
    const isOptional = category.optional === 'true';

    let accumulatedQty = 0; // category items quantity
    const categoryItems = []; // Category items to add to bundle

    for (const lineItem of lineItems) {
      const product = category.products.find((p) => p.id === lineItem.product_id);
      if (product && lineItem.quantity > 0) {
        const qtyToAdd = Math.min(lineItem.quantity, requiredQty - accumulatedQty);

        if (qtyToAdd > 0) {
          categoryItems.push({
            key: lineItem.key,
            id: lineItem.variant_id,
            title: product.title,
            qty: qtyToAdd,
            cl: `${category.id}`,
            uid: product.variants[lineItem.variant_id] || '',
          });

          accumulatedQty += qtyToAdd;
          lineItem.quantity -= qtyToAdd;
        }
      }

      // If we have enough items for the category, we can stop
      if (accumulatedQty >= requiredQty) {
        break;
      }
    }

    // If we don't have enough items for the category and it's required, we can't build the bundle
    if (!isOptional && accumulatedQty < requiredQty) {
      return null;
    }

    // Add category items to bundle
    bundle.push(...categoryItems);
  }

  return {
    add: {
      id: `${window.TMW.settings.bundleProduct}`,
      quantity: 1,
      attributes: [
        {
          key: '_bundle_id',
          value: getUniqueID(),
        },
        ...(bundleConfig.image ? [
          {
            key: '_bundle_image',
            value: `${bundleConfig.image}`,
          },
        ] : []),
        {
          key: '_bundle_uid',
          value: bundleConfig.uid,
        },
        {
          key: '_bundle_data',
          value: JSON.stringify({
            items: bundle.map((item) => {
              const {key, ...component} = item;
              return component;
            }),
            type: bundleConfig.title,
          })
        }
      ]
    },
    updates: getBundleCartUpdates(bundle, items),
  };
}

export function combineBundles(bundles, cartState) {
  if (!bundles || !bundles.length || !cartState || !cartState.items?.length || !window.TMW.settings.bundleProduct) {
    return [];
  }
  for (const bundleConfig of bundles) {
    const bundle = getBundleFromLineItems(bundleConfig, cartState.items);
    if (bundle) {
      return bundle;
    }
  }
  return null;
}

/**
 * Get list of items to update in cart for bundle
 * @param bundle
 * @param lineItems
 * @returns {{quantity: *, id: *}[]}
 */
export function getBundleCartUpdates(bundle, lineItems) {
  const updates = {};
  const originalQty = Object.fromEntries(lineItems.map((item) => [item.key, item.quantity]));
  bundle.forEach((item) => {
    if (!updates[item.key]) {
      updates[item.key] = originalQty[item.key] || 0;
    }
    updates[item.key] -= item.qty;
  });
  return Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
}

/**
 * Get list of available bundles
 * @returns {Promise<{length}|any|*[]>}
 */
export async function getBundlesList() {
  const response = await fetch(`${window.Shopify.routes.root}cart?view=bundles`).then((res) => res.json())
  .catch((err) => {
    throw new Error(`Failed to fetch bundles: ${err.message}`);
  });
  if (response && response.length) {
    return response;
  }
  return [];
}